// Copiloto de IA no chat do atendente (WootDash /conversas/{id}/copiloto): estado, envio automático e decisão.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'
import type { CopilotSuggestionNotification } from '@/composables/useConversationRealtime'

export interface CopilotState {
  /** O Fluxo da conversa tem agente de IA copiloto ligado. */
  active: boolean
  agentId: string | null
  agentName: string | null
  flowNumber: number | null
  /** Check "enviar automaticamente" desta conversa. */
  autoSend: boolean
  /** Última sugestão ainda não usada (pra quem abre a conversa depois que ela chegou). */
  pending: CopilotSuggestionNotification | null
  /** O dia de quem está olhando com o copiloto (contador discreto da barra). */
  today?: CopilotDay | null
}

export interface CopilotDay { used: number; edited: number; discarded: number; helpful: number; notHelpful: number }

export type CopilotTone = 'curta' | 'formal' | 'acolhedora'
export interface CopilotRewriteResult { success: boolean; problem: string | null; text: string | null; tone: CopilotTone | null }

export type CopilotDecision = 'used' | 'edited' | 'discarded'

function headers(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function getCopilotState(token: string, conversationId: string): Promise<CopilotState | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas/${conversationId}/copiloto`, { headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function setCopilotAutoSend(token: string, conversationId: string, ativo: boolean): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas/${conversationId}/copiloto/automatico`, {
      method: 'PUT', headers: headers(token, true), body: JSON.stringify({ ativo }),
    })
    return res.ok
  } catch {
    return false
  }
}

export async function requestCopilotSuggestion(token: string, conversationId: string): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/conversas/${conversationId}/copiloto/sugerir`, { method: 'POST', headers: headers(token) })
    return res.ok
  } catch {
    return false
  }
}

/** Melhor esforço: serve só pra medir o quanto o copiloto ajuda — erro aqui não atrapalha o atendimento. */
export async function decideCopilotSuggestion(token: string, suggestionId: string, situacao: CopilotDecision): Promise<void> {
  try {
    await fetch(`${WOOTDASH_BASE}/copiloto/sugestoes/${suggestionId}/decisao`, {
      method: 'POST', headers: headers(token, true), body: JSON.stringify({ situacao }),
    })
  } catch { /* só métrica */ }
}

/** O que o atendente fez com o texto sugerido: enviou igual, editou ou enviou outra coisa. */
export function decisionFor(suggested: string, sent: string): CopilotDecision {
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim()
  if (norm(sent) === norm(suggested)) return 'used'
  return norm(sent).length > 0 && similarity(norm(sent), norm(suggested)) >= 0.5 ? 'edited' : 'discarded'
}

/** Parecença grosseira por palavras em comum (Jaccard) — bom o bastante pra separar "editou" de "escreveu outra". */
function similarity(a: string, b: string): number {
  const wa = new Set(a.toLowerCase().split(' '))
  const wb = new Set(b.toLowerCase().split(' '))
  const common = [...wa].filter(w => wb.has(w)).length
  return common / Math.max(1, new Set([...wa, ...wb]).size)
}


/** Mesma resposta noutro tom (curta, formal, acolhedora). Gasta tokens do teto do dia; o texto novo fica na sugestão. */
export async function rewriteCopilotSuggestion(token: string, suggestionId: string, tom: CopilotTone): Promise<CopilotRewriteResult> {
  const failed = (problem: string): CopilotRewriteResult => ({ success: false, problem, text: null, tone: null })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/copiloto/sugestoes/${suggestionId}/tom`, { method: 'POST', headers: headers(token, true), body: JSON.stringify({ tom }) })
    const data = await res.json().catch(() => null)
    return res.ok && data && typeof data.success === 'boolean' ? data : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

/** 👍/👎 na sugestão (melhor esforço — só métrica). */
export async function rateCopilotSuggestion(token: string, suggestionId: string, util: boolean): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/copiloto/sugestoes/${suggestionId}/avaliacao`, { method: 'POST', headers: headers(token, true), body: JSON.stringify({ util }) })
    return res.ok
  } catch {
    return false
  }
}
